import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import map from "../../data/cgDistricts.json";
import { districtName } from "../../data/districts";
import { useLang } from "../../i18n/LangContext";
import { useSite } from "../../lib/SiteContext";
import { Reveal, Overline } from "./Reveal";

export const CoverageMap = () => {
  const { t, lang } = useLang();
  const { settings, districtCounts } = useSite();
  const [hover, setHover] = useState(null);
  const counts = districtCounts ?? {};
  const covered = new Set([...(Array.isArray(settings.covered_districts) ? settings.covered_districts : []), ...Object.keys(counts).filter((k) => counts[k] > 0)]);
  if (!covered.size) return null;
  const c = t.coverage;
  const totalShops = Object.values(counts).reduce((a, b) => a + b, 0);
  const label = (name) => (counts[name] ? `${counts[name]} ${counts[name] === 1 ? c.shop : c.shops}` : covered.has(name) ? c.active : c.soon);
  const list = map.districts.filter((d) => covered.has(d.name));
  const hovered = map.districts.find((d) => d.name === hover);

  return (
    <section id="coverage" className="py-24 lg:py-32" data-testid="coverage-map-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <Overline>{c.over}</Overline>
          <h2 className="section-title mt-5">{c.title}</h2>
          <p className="mt-6 font-display text-6xl font-extrabold text-white" data-testid="coverage-count">
            {list.length}<span className="text-2xl text-slate-400"> / {map.districts.length}</span>
          </p>
          <p className="mt-2 text-sm text-slate-300">{c.districts}{totalShops > 0 && <> · <span className="font-semibold text-white" data-testid="coverage-total-shops">{totalShops}</span> {c.shops}</>}</p>
          <ul className="mt-8 flex flex-wrap gap-2" data-testid="coverage-list">
            {list.map((d) => (
              <li key={d.name}>
                <button
                  onMouseEnter={() => setHover(d.name)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => setHover(d.name)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors duration-300 ${hover === d.name ? "border-saffron bg-saffron text-ink" : "border-white/15 text-slate-200 hover:border-saffron/60"}`}
                  data-testid={`coverage-chip-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <MapPin size={11} /> {districtName(d.name, lang)}{counts[d.name] ? <span className="ml-1 font-mono opacity-80">{counts[d.name]}</span> : null}
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs text-slate-400">{c.tap} {c.note}</p>
        </Reveal>

        <Reveal delay={0.1} className="relative lg:col-span-7">
          <div className="relative mx-auto max-w-[460px]">
            <svg viewBox={`-10 -10 ${map.w + 20} ${map.h + 20}`} className="h-auto w-full" role="img" aria-label={c.title} data-testid="coverage-svg">
              {map.districts.map((d, i) => {
                const on = covered.has(d.name);
                const hot = hover === d.name;
                return (
                  <motion.path
                    key={d.name}
                    d={d.d}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.6, delay: i * 0.03 }}
                    fill={on ? (hot ? "#FF8A33" : "#FF6B00") : hot ? "#2A3A5E" : "#18233D"}
                    stroke="#0B1120"
                    strokeWidth={1.6}
                    style={{ cursor: "pointer", transition: "fill .3s" }}
                    onMouseEnter={() => setHover(d.name)}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => setHover(d.name)}
                    data-testid={`district-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
                  />
                );
              })}
              {list.map((d) => (
                <circle key={d.name} cx={d.cx} cy={d.cy} r={5} fill="#0B1120" stroke="#fff" strokeWidth={2} pointerEvents="none" />
              ))}
            </svg>
            {hovered && (
              <div
                className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-lg border border-white/15 bg-ink/90 px-3 py-1.5 text-xs text-white backdrop-blur"
                style={{ left: `${((hovered.cx + 10) / (map.w + 20)) * 100}%`, top: `${((hovered.cy + 10) / (map.h + 20)) * 100 - 2}%` }}
                data-testid="coverage-tooltip"
              >
                {districtName(hovered.name, lang)}
                <span className={`ml-2 ${covered.has(hovered.name) ? "text-emerald-300" : "text-slate-400"}`} data-testid="coverage-tooltip-count">{label(hovered.name)}</span>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-center gap-6 text-xs text-slate-300">
            <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-saffron" />{c.active}</span>
            <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-[#18233D] ring-1 ring-white/20" />{c.soon}</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
